import { Component, Input, OnChanges } from '@angular/core';
import { ColorsConfig, HoldingsComponentDCR } from '@ddt-web/config';
import { ComponentData, DataServiceApiConstants, ProductPageLinkResolverService, SecurityDataUtil } from '@ddt-web/data-service';
import { PortfolioSubstitutionAlerts } from '@ddt-web/domain';
import { HoldingsUtil, Memoize } from '@ddt-web/generic-utils';
import { AlertConstants, AlertUtil, PartnerHoldingsAlertsService } from '@ddt-web/partner-integrations';
import { UrlService } from '@ddt-web/url';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { HoldingsTableUtil } from '../../utils';

@Component({
	selector: 'hl-holdings-table',
	templateUrl: './holdings-table.component.html',
	styleUrls: ['./holdings-table.component.scss']
})
export class HoldingsTableComponent implements OnChanges {
	@Input() public data: ComponentData;

	public categoryColumnClass: string;
	public dataColumnClass: string;
	public descriptionColumnClass: string;
	public dataEmptyColumnClass: string;
	public dataSpanClass: string;
	public extraDataColumnClass: string;
	public tickerClass: string;
	public iconColumnClass: string;

	public categoryIndexMap: Map<string, Array<number>>;
	public categoryWeightMap: Map<string, Array<number>>;
	public categoriesArray: Array<string>;
	public productIdPerTickerMap: Map<string, number>;
	public securityIdIndex: number;

	// Partner Alerts
	public invalidSecuritySubstitutionsAlerts: Array<Map<string, string>> = [];
	public notTradableSecuritiesList: Array<string> = [];
	public portfolioAssetSubstitutionsAlerts: Array<Map<string, string>> = [];
	public portfolioSubstitutionAlerts: Array<PortfolioSubstitutionAlerts> = [];

	public tableData;
	public colorsConfig = ColorsConfig.ASSET_CLASS_COLORS;
	public AlertConstants = AlertConstants;
	public showAssetSubstitutionsDisclosure: boolean;
	public showViewStructureInfo: Array<string>;
	public assetSubstitutionDisclosure = HoldingsComponentDCR.ASSET_CLASS_OR_BOND_SUBSTITUTION;
	public portfolioIds: any[] = [];
	public fteTransitionId: string = DataServiceApiConstants.FTE_TRANSITION_ID;


	constructor(
		private partnerHoldingsAlertsService: PartnerHoldingsAlertsService,
		private urlService: UrlService,
		private linkResolverService: ProductPageLinkResolverService
	) { }

	public ngOnChanges(): void {
		if (this.data.success && !this.data.tabs[0].error) {
			this.tableData = this.data.tabs[0].tableData[0];
			this.categoryIndexMap = HoldingsTableUtil.populateCategoryIndexMap(this.data.tabs[0].tableData[0]);
			this.categoryWeightMap = HoldingsTableUtil.populateCategoryWeightMap(this.data.tabs[0].tableData[0], this.categoryIndexMap);
			this.productIdPerTickerMap = SecurityDataUtil.populateProductIdPerTickerMap(this.data.tabs[0]);
			this.categoriesArray = Array.from(this.categoryIndexMap.keys());

			// requestId is securityId - always required to be identifier/securityId for partner integration
			this.securityIdIndex = this.tableData.cells[0].findIndex(column => column.config.id === 'requestId');

			this.assignColumnClasses(this.data.portfolioData.length);
			this.portfolioIds = this.data.portfolioData.map(portfolio => get(portfolio, 'field.id.formattedValue'));

			// PARTNER mode
			const portfolioSecuritiesTab = this.data.getTabById('portfolio.securities');
			const portfolioFlagsTab = this.data.getTabById('portfolio.flags');
			if (portfolioFlagsTab && !portfolioFlagsTab.error) {
				this.notTradableSecuritiesList = this.partnerHoldingsAlertsService.populateNotTradableSecuritiesList(portfolioFlagsTab);
			}
			if (portfolioSecuritiesTab && portfolioSecuritiesTab.tableData.length && !portfolioSecuritiesTab.error) {
				this.portfolioSubstitutionAlerts = this.partnerHoldingsAlertsService.getPortfolioSubstitutionAlerts(portfolioSecuritiesTab);
				this.showAssetSubstitutionsDisclosure = AlertUtil.hasMultiplePortfolioAssetSubstitutionAlerts(this.portfolioSubstitutionAlerts);
				this.invalidSecuritySubstitutionsAlerts = this.portfolioSubstitutionAlerts.map(alertPerPortfolio => alertPerPortfolio.invalidSecuritiesAlertsMsgMap);
				this.portfolioAssetSubstitutionsAlerts = this.portfolioSubstitutionAlerts.map(alertPerPortfolio => alertPerPortfolio.assetClassAlertsMsgMap);
			}
		}
	}

	public showSecurityNotTradableAlert(rowIndex: number): boolean {
		return this.notTradableSecuritiesList && this.notTradableSecuritiesList.includes(this.tableData.headersY[rowIndex].formattedValue);
	}

	@Memoize()
	public showSecurityPortfolioAlert(portfolioIndex: number): boolean {
		const showPortfolioAlert = !isEmpty(this.portfolioSubstitutionAlerts) &&
			AlertUtil.hasInvalidSecuritySubstitutionAlerts(this.portfolioSubstitutionAlerts[portfolioIndex]) ||
			AlertUtil.hasPortfolioAssetSubstitutionAlerts(this.portfolioSubstitutionAlerts[portfolioIndex]);
		return showPortfolioAlert;
	}

	public showSecurityPadding(rowIndex: number): boolean {
		return (
			// Only if no security not tradable alert
			!this.showSecurityNotTradableAlert(rowIndex) &&
			// Only for multiple portfolios
			this.tableData.portfolioData.length > 1
		);
	}

	@Memoize()
	public isCashSubstitutionAlert({ rowIndex, portfolioIndex }: AlertClassificationParams): boolean {
		const securityId = this.tableData.cells[rowIndex][this.securityIdIndex].formattedValue;
		const invalidSecurityAlertsPerPortfolio = this.invalidSecuritySubstitutionsAlerts[portfolioIndex];
		return HoldingsUtil.isCashIdentifier(securityId) && HoldingsTableUtil.showSubstitutedFromAlert(invalidSecurityAlertsPerPortfolio, securityId);
	}

	@Memoize()
	public isInvalidSecurityAlert({ rowIndex, portfolioIndex }: AlertClassificationParams): boolean {
		const securityId = this.tableData.cells[rowIndex][this.securityIdIndex].formattedValue;
		const invalidSecurityAlertsPerPortfolio = this.invalidSecuritySubstitutionsAlerts[portfolioIndex];
		return !HoldingsUtil.isCashIdentifier(securityId) && HoldingsTableUtil.showSubstitutedFromAlert(invalidSecurityAlertsPerPortfolio, securityId);
	}

	@Memoize()
	public isSecurityAssetClassAlert({ rowIndex, portfolioIndex }: AlertClassificationParams): boolean {
		const securityId = this.tableData.cells[rowIndex][this.securityIdIndex].formattedValue;
		const assetSubstitutionsAlertsPerPortfolio = this.portfolioAssetSubstitutionsAlerts[portfolioIndex];
		return HoldingsTableUtil.showSubstitutedFromAlert(assetSubstitutionsAlertsPerPortfolio, securityId);
	}

	@Memoize()
	public populatePageUrl(rowIndex: number): string {
		const portfolioId = this.productIdPerTickerMap.get(this.tableData.headersY[rowIndex].formattedValue);
		return this.linkResolverService.getUrlByCellData(this.tableData.cells[rowIndex], 'assetTypeClass', portfolioId?.toString());
	}

	public assignColumnClasses(numberOfPortfolios: number): void {
		switch (numberOfPortfolios) {
			case 1:
				this.categoryColumnClass = 'col-12 col-md-18';
				this.dataColumnClass = 'col-12 col-md-6';
				this.dataEmptyColumnClass = 'col-10 col-md-5';
				this.dataSpanClass = 'col-14 col-md-19';
				this.descriptionColumnClass = 'col-9 col-md-14';
				this.extraDataColumnClass = 'col-12 col-md-6';
				this.tickerClass = 'col-3 col-md-4';
				break;
			case 2:
				this.categoryColumnClass = this.urlService.hasSpecificPortfolioId(DataServiceApiConstants.FTE_TRANSITION_ID) ? 'col-10 col-md-8' : 'col-9 col-md-8';
				this.dataColumnClass = this.urlService.hasSpecificPortfolioId(DataServiceApiConstants.FTE_TRANSITION_ID) ? 'col-7 col-md-8' : 'col-5 col-md-7';
				this.descriptionColumnClass = this.urlService.hasSpecificPortfolioId(DataServiceApiConstants.FTE_TRANSITION_ID) ? 'col-7 col-md-6' : 'col-6 col-md-6';
				this.tickerClass = 'col-3 col-md-2';
				this.dataEmptyColumnClass = 'col-6 col-md-6';
				this.dataSpanClass = 'col-18 col-md-18';
				this.extraDataColumnClass = 'col-5 col-md-2';
				break;
			case 3:
			case 4:
			case 5:
				this.categoryColumnClass = 'col-9 col-md-6';
				this.dataColumnClass = 'col-5 col-md-6';
				this.dataEmptyColumnClass = 'col-6 col-md-5';
				this.dataSpanClass = 'col-18 col-md-19';
				this.descriptionColumnClass = 'col-6 col-md-4';
				this.tickerClass = 'col-3 col-md-2';
				break;
		}
	}
}

interface AlertClassificationParams { rowIndex: number; portfolioIndex: number }


